import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Option } from '../../../core/models';
import { TranslatePipe } from '@core/pipes';
import { AsyncPipe } from '@angular/common';
import { SvgComponent } from '../svg/svg.component';
import { ControlValueAccessorBase } from '../../../core/control-value-accessor-base';
import { OptionComponent } from '../option/option.component';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  Overlay,
  ScrollStrategy,
} from '@angular/cdk/overlay';

@Component({
  selector: 'sc-select',
  imports: [TranslatePipe, AsyncPipe, SvgComponent, CdkConnectedOverlay, CdkOverlayOrigin],
  templateUrl: './select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SelectComponent,
    },
  ],
})
export class SelectComponent
  extends ControlValueAccessorBase<string>
  implements AfterContentChecked
{
  private optionList: OptionComponent[] = [];
  placeholder = input<string>('');
  isDisabled = input<boolean>(false);
  selectedOption = signal<Option>(null);
  isOpen = signal(false);
  activeIndex: number = null;
  selectButtonRef = viewChild<ElementRef>('selectButtonRef');

  public outSelectedOption = output<string>();

  public scrollStrategy: ScrollStrategy = this.overlay.scrollStrategies.close();

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly overlay: Overlay
  ) {
    super();
  }

  public toggleOpen() {
    if (this.disabled) {
      return;
    }
    this.isOpen.set(!this.isOpen());
  }

  public ngAfterContentChecked() {
    const optionComponentControlIndex = this.optionList.findIndex((optionComponent) => {
      return optionComponent.option.id === this.value;
    });
    const optionComponentControl = this.optionList[optionComponentControlIndex];

    if (this.activeIndex === null) {
      this.activeIndex = optionComponentControlIndex;
      this.optionList[this.activeIndex]?.focus();
    }

    if (optionComponentControl) {
      optionComponentControl.isSelected = true;
      this.selectedOption.set(optionComponentControl.option);
    }
  }

  addOptionComponent(option: OptionComponent) {
    this.optionList.push(option);
  }

  selectOptionComponent(optionComponent: OptionComponent) {
    this.optionList.forEach((radioButtonComponent) => {
      radioButtonComponent.isSelected = false;
      radioButtonComponent.cdr.markForCheck();
    });
    optionComponent.isSelected = true;
    this.value = optionComponent.option.id;
    this.selectedOption.set(optionComponent.option);
    this.writeValue(this.value);
    this.onChange(this.value);
    this.isOpen.set(false);
    this.cdr.detectChanges();

    this.outSelectedOption.emit(optionComponent.option.id);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.isOpen()) {
      return;
    }

    if (event.key === 'Escape') {
      this.isOpen.set(false);
      this.activeIndex = 0;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex--;
      if (this.activeIndex < 0) {
        this.activeIndex = this.optionList.length - 1;
      }
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeIndex++;
      if (this.activeIndex > this.optionList.length - 1) {
        this.activeIndex = 0;
      }
    }

    this.optionList.forEach((optionComponent) => (optionComponent.isActive = false));
    this.optionList[this.activeIndex]?.focus();

    if (event.key === 'Enter') {
      this.selectButtonRef().nativeElement?.focus();
      this.selectOptionComponent(this.optionList[this.activeIndex]);
      setTimeout(() => {
        this.isOpen.set(false);
      }, 0);
    }
  }
}
